import React, {
	useCallback, useEffect, useRef, useState,
} from 'react';
import {
	Button, Collapse, Modal, Tag,
} from 'antd-mobile';
import {
	message, Tooltip,
} from 'antd';
import { CaretRightOutlined, CheckCircleFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { DashboardContentType, QuestionStatus } from '../../config';
import { logger } from '../../libs/utils/logger';

import { ReactComponent as LockIcon } from '../../assets/icons/lock-icon.svg';

import styles from './dashboard-segment-mobile.module.css';

interface DashboardSegmentProps {
	quizName: string
	segmentIndex: number
	segmentData: QuizDashboardSegmentData
	showSubmit: boolean
	sendSubmitButtonEvent: (data: SubmissionPayload) => void,
	style?: React.CSSProperties,
	onSubmit: (segmentIndex: number) => Promise<void>,
}

export const DashboardSegment: React.FunctionComponent<DashboardSegmentProps> = (props) => {
	const {
		quizName, segmentData, segmentIndex, showSubmit, style, onSubmit, sendSubmitButtonEvent,
	} = props;
	const hasQuestions = segmentData.questions.length > 0;

	const segmentSubmissionButtonPressTime = useRef<string>('');

	useEffect(() => {
		segmentSubmissionButtonPressTime.current = '';
	}, [showSubmit]);

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [activeKeys, setActiveKeys] = useState<string[]>(
		(segmentData.isDisabled || segmentData.isSubmitted) ? [] : [`${segmentData._id}-panel`],
	);

	const handleSubmitSegment = useCallback(async () => {
		try {
			if (sendSubmitButtonEvent) {
				sendSubmitButtonEvent({
					segmentSubmissionPressTime: segmentSubmissionButtonPressTime.current,
					yesButtonPressTime: new Date().toString(),
					index: segmentIndex,
				});
			}
			setIsSubmitting(true);
			await onSubmit(segmentIndex);
		} catch (e: any) {
			logger.error(e);

			message.error(e.message);
		} finally {
			setIsSubmitting(false);
		}
	}, [segmentIndex, onSubmit, sendSubmitButtonEvent]);

	const toggleSegment = useCallback(() => {
		if (activeKeys.includes(`${segmentData._id}-panel`)) {
			setActiveKeys([]);
		} else {
			setActiveKeys([`${segmentData._id}-panel`]);
		}
	}, [segmentData, activeKeys]);

	useEffect(() => {
		// auto open next segment on submit. it's for force rerendering
		if (!(segmentData.isSubmitted || segmentData.isDisabled)) {
			setActiveKeys([`${segmentData._id}-panel`]);
		}
	}, [segmentData]);

	return (
		<div className={`${styles.dashboardSegmentContainer} ${activeKeys.includes(`${segmentData._id}-panel`) ? styles.active : ''}`} style={style}>
			{
				(() => {
					if (segmentData.isSubmitted) {
						return (
							<Collapse
								className={`${styles.dashboardSegment} ${styles.submitted} ${activeKeys.includes(`${segmentData._id}-panel`) ? styles.active : ''}`}
								activeKey={activeKeys}
								arrowIcon={(active) => (
									<CaretRightOutlined
										rotate={active ? 90 : 0}
										onClick={toggleSegment}
									/>
								)}
								onChange={setActiveKeys}
							>
								<Collapse.Panel
									title={(
										<div className={styles.segmentHeader} style={{ display: 'flex', flexDirection: 'column' }}>
											<div>
												<span>
													Section
													{' '}
													{String.fromCharCode(segmentIndex + 65)}
												</span>
												<span>
													{' - '}
													{segmentData.title}
												</span>
											</div>
											<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
												<span>
													<CheckCircleFilled className={styles.segmentCheckIcon} />
												</span>
												<span>
													{
														segmentData.questions.filter(
															(el) => el.status === QuestionStatus.Submitted,
														).length
													}
													/
													{segmentData.questions.length}
													{' '}
													Questions submitted successfully
												</span>
											</div>
										</div>
									)}
									key={`${segmentData._id}-panel`}
									style={{ position: 'relative' }}
									disabled={isSubmitting}
								>
									<div className={`${styles.segmentData} ${!showSubmit ? styles.revisitAllowed : ''}`}>
										{hasQuestions ? (
											segmentData.questions.map((question, idx) => (
												<div
													key={question._id}
													style={{
														display: 'flex',
														alignItems: 'flex-start',
														background: 'transparent',
														borderBottom: idx !== segmentData.questions.length - 1 ? '1px solid #f0f0f0' : 'none',
														padding: '12px 16px',
														marginBottom: 4,
														minWidth: '100%',
													}}
												>
													<div style={{
														fontWeight: 'bold', color: '#de6834', minWidth: 24, marginRight: 8, fontSize: 16,
													}}
													>
														{idx + 1}
														.
													</div>
													<div style={{ flex: 1 }}>
														<div style={{
															fontSize: 15, fontWeight: 500, color: '#222', marginBottom: 4,
														}}
														>
															{question.title}
														</div>
														<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
															<Tag color="primary" fill="outline" style={{ fontWeight: 500 }}>
																{DashboardContentType[question.type as number]}
															</Tag>
															<Tag color={question.status === QuestionStatus.Submitted ? 'success' : 'danger'} fill={question.status === QuestionStatus.Submitted ? 'solid' : 'outline'} style={{ fontWeight: 500 }}>
																{QuestionStatus[question.status as number]}
															</Tag>
														</div>
													</div>
													<div style={{ minWidth: 90, textAlign: 'right', alignSelf: 'center' }}>
														{/* No action for submitted section */}
													</div>
												</div>
											))
										) : (
											<div style={{ padding: '12px 16px', color: '#666', fontSize: 14 }}>
												No questions in this section.
											</div>
										)}
									</div>
								</Collapse.Panel>
							</Collapse>
						);
					} if (segmentData.isDisabled) {
						return (
							<div className={`${styles.dashboardSegment} ${styles.disabled}`}>
								<Tooltip
									title="Submit previous section to unlock this section."
									arrowPointAtCenter
									placement="bottom"
									overlayStyle={{
										maxWidth: 'unset',
										fontFamily: 'Hind, sans-serif',
										fontSize: 13,
									}}
								>
									<div className={styles.segmentHeader}>
										<div>
											<span>
												Section
												{' '}
												{String.fromCharCode(segmentIndex + 65)}
											</span>
											<span>
												{' - '}
												{segmentData.title}
											</span>

											<span>
												<LockIcon />
											</span>
										</div>
										<div>
											<span>
												{segmentData.questions.length}
												{' '}
												Questions
											</span>
										</div>
									</div>
								</Tooltip>
							</div>
						);
					}

					return (
						<Collapse
							className={`${styles.dashboardSegment} ${activeKeys.includes(`${segmentData._id}-panel`) ? styles.active : ''}`}
							activeKey={activeKeys}
							arrowIcon={(active) => (
								<CaretRightOutlined
									rotate={active ? 90 : 0}
									onClick={toggleSegment}
									style={{ padding: '0.25rem' }}
								/>
							)}
							onChange={setActiveKeys}
						>
							<Collapse.Panel
								title={(
									<div className={styles.segmentHeader} style={{ display: 'flex', flexDirection: 'column' }}>
										<div>
											<span>
												Section
												{' '}
												{String.fromCharCode(segmentIndex + 65)}
											</span>
											<span>
												{' - '}
												{segmentData.title}
											</span>
										</div>
										<div>
											<span>
												{
													segmentData.questions.filter(
														(el) => el.status === QuestionStatus.Submitted,
													).length
												}
												/
												{segmentData.questions.length}
												{' '}
												Questions
											</span>
										</div>
									</div>
								)}
								key={`${segmentData._id}-panel`}
								style={{ position: 'relative' }}
								disabled={isSubmitting}
							>
								<div className={`${styles.segmentData} ${!showSubmit ? styles.revisitAllowed : ''}`}>
									{hasQuestions ? (
										segmentData.questions.map((question, idx) => (
											<div
												key={question._id}
												style={{
													display: 'flex',
													alignItems: 'flex-start',
													background: 'transparent',
													// borderRadius: 8,
													// boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
													borderBottom: idx !== segmentData.questions.length - 1 ? '1px solid #f0f0f0' : 'none',
													padding: '12px 16px',
													marginBottom: 4,
													minWidth: '100%',
												}}
											>
												<div style={{
													fontWeight: 'bold', color: '#de6834', minWidth: 24, marginRight: 8, fontSize: 16,
												}}
												>
													{idx + 1}
													.
												</div>
												<Link
													to={`/test/${quizName}/attempt/${question._id}`}
													style={{
														color: '#de6834', fontWeight: 600, fontSize: 14, textDecoration: 'none',
													}}
												>
													<div style={{
														fontSize: 15, fontWeight: 500, color: '#222', marginBottom: 4,
													}}
													>
														{question.title}
													</div>
													<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
														<Tag color="primary" fill="outline" style={{ fontWeight: 500 }}>
															{DashboardContentType[question.type as number]}
														</Tag>
														<Tag color={question.status === QuestionStatus.Submitted ? 'success' : 'danger'} fill={question.status === QuestionStatus.Submitted ? 'solid' : 'outline'} style={{ fontWeight: 500 }}>
															{QuestionStatus[question.status as number]}
														</Tag>
													</div>
												</Link>
											</div>
										))
									) : (
										<div style={{ padding: '12px 16px', color: '#666', fontSize: 14 }}>
											No questions in this section.
										</div>
									)}
									{showSubmit && (
										<Button
											type="button"
											color="primary"
											size="middle"
											shape="rounded"
											loading={isSubmitting}
											style={{ marginTop: 12 }}
											onClick={() => {
												segmentSubmissionButtonPressTime.current = new Date().toString();
												Modal.confirm({
													content: (
														<p>
															Do you really want to submit
															{' '}
															<span style={{ color: '#de6834' }}>{segmentData.title}</span>
															{' section? '}
															<br />
															You won&apos;t be able to submit questions from this section again.
														</p>
													),
													confirmText: 'Yes',
													cancelText: 'No',
													onConfirm: handleSubmitSegment,
													bodyStyle: { maxWidth: '300px', maxHeight: '290px' },
												});
											}}
										>
											submit this section
										</Button>
									)}
								</div>
							</Collapse.Panel>
						</Collapse>
					);
				})()
			}
		</div>
	);
};
